import React, { useState } from 'react';
import { Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { Refresh as RefreshIcon, WifiOff as WifiOffIcon } from '@mui/icons-material';

/**
 * Shown when the user is offline and either has no offline auth snapshot
 * or the snapshot is past its 7-day TTL. Provides a single action to retry
 * connectivity; once the device is back online, the auth context will
 * revalidate via /api/auth/me automatically.
 */
export const OfflineExpired: React.FC<{
  onRetry?: () => void;
  reason?: 'expired' | 'missing';
}> = ({ onRetry, reason = 'expired' }) => {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      if (typeof onRetry === 'function') {
        await onRetry();
        return;
      }
      // Default: hint the page to refresh; on iOS/Safari this re-runs auth bootstrap.
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    } finally {
      setRetrying(false);
    }
  };

  const headline =
    reason === 'expired'
      ? 'Offline access expired'
      : 'Offline access not yet set up';
  const detail =
    reason === 'expired'
      ? 'Your 7-day offline window has ended. Reconnect to a network to sign in and restore offline access.'
      : 'Sign in once while connected to set up offline access on this device.';

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={2}
        sx={{
          p: 4,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'error.light',
            color: 'error.contrastText',
          }}
        >
          <WifiOffIcon sx={{ fontSize: 36 }} />
        </Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {headline}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {detail}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={retrying ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={handleRetry}
          disabled={retrying}
          sx={{ mt: 1 }}
        >
          {retrying ? 'Retrying…' : 'Retry connection'}
        </Button>
      </Paper>
    </Container>
  );
};

export default OfflineExpired;
